var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Box, Drawer as MuiDrawer, styled, useTheme, } from '@mui/material';
import { Expand2Left12, Expand2Right12 } from '@yunex/yds-icons';
import React, { createContext, useContext, useState } from 'react';
import Button from '../Button';
import SvgIcon from '../SvgIcon';
import { rem } from '../../utils/rem';
var styles = function (theme) { return ({
    drawerOpen: {
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    drawerClose: {
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: 'hidden',
        width: rem(57),
        '& [class*=MuiList]': {
            '& [class*=MuiListItemText-root]': {
                opacity: 0,
            },
            '& [class*=MuiListSubheader-root]': {
                opacity: 0,
                lineHeight: 0,
                paddingTop: 0,
                paddingBottom: 0,
            },
            '& [class*=MuiDivider-root]': {
                opacity: 0,
                height: 0,
                marginTop: 0,
                marginBottom: 0,
            },
        },
    },
}); };
var SDrawer = styled(MuiDrawer, {
    shouldForwardProp: function (prop) { return !['drawerOpen', 'collapsible'].includes(prop.toString()); },
})(function (_a) {
    var theme = _a.theme, collapsible = _a.collapsible, drawerOpen = _a.drawerOpen;
    var classes = styles(theme);
    if (!collapsible) {
        return {};
    }
    return __assign(__assign(__assign({}, (drawerOpen && classes.drawerOpen)), (!drawerOpen && classes.drawerClose)), { '& [class*=MuiDrawer-paper]': __assign(__assign({}, (drawerOpen && classes.drawerOpen)), (!drawerOpen && classes.drawerClose)) });
});
var DrawerContent = styled(Box)({ flexGrow: 1, overflow: 'auto' });
var DrawerFooter = styled('div')(function (_a) {
    var anchor = _a.anchor;
    return ({
        display: 'flex',
        alignItems: 'center',
        padding: rem(12),
        paddingTop: rem(4),
        justifyContent: anchor === 'left' ? 'flex-end' : 'flex-start',
    });
});
var DrawerContext = createContext({ open: false });
/**
 * Create a context around drawer, so we can read open state in children components. Useful for SideNav.
 */
var DrawerContextProvider = function (_a) {
    var open = _a.open, collapsible = _a.collapsible, children = _a.children;
    return React.createElement(DrawerContext.Provider, { value: { open: open, collapsible: collapsible } }, children);
};
export var useDrawerContext = function () {
    var context = useContext(DrawerContext);
    // Components that are not wrapped in the provider will receive undefined as a value. This can happen when using the component outside of the drawer.
    if (context === undefined) {
        return { open: undefined, collapsible: undefined };
    }
    return context;
};
var Drawer = function (_a) {
    var openProp = _a.open, collapsible = _a.collapsible, defaultOpen = _a.defaultOpen, onCollapseToggle = _a.onCollapseToggle, className = _a.className, drawerContentProps = _a.drawerContentProps, props = __rest(_a, ["open", "collapsible", "defaultOpen", "onCollapseToggle", "className", "drawerContentProps"]);
    var theme = useTheme();
    // A component can be considered controlled when its value prop is not undefined.
    var isControlled = openProp !== undefined;
    // When a component is not controlled, it can have a defaultValue.
    var hasDefaultValue = defaultOpen !== undefined;
    var _b = useState(hasDefaultValue ? defaultOpen : false), internalOpen = _b[0], setOpen = _b[1];
    var drawerOpen = isControlled ? openProp : internalOpen;
    var onButtonClick = function (e) {
        if (isControlled) {
            onCollapseToggle === null || onCollapseToggle === void 0 ? void 0 : onCollapseToggle(e);
        }
        else {
            setOpen(function (o) { return !o; });
        }
    };
    // The 'anchor' prop defaults to 'left'.
    var isLeftAnchor = !props.anchor || props.anchor === 'left';
    var closedIcon = isLeftAnchor ? Expand2Right12 : Expand2Left12;
    var openedIcon = isLeftAnchor ? Expand2Left12 : Expand2Right12;
    return (React.createElement(DrawerContextProvider, { open: drawerOpen !== null && drawerOpen !== void 0 ? drawerOpen : false, collapsible: collapsible !== null && collapsible !== void 0 ? collapsible : false },
        React.createElement(SDrawer, __assign({}, __assign({ collapsible: collapsible }, props), { drawerOpen: drawerOpen, open: !collapsible && drawerOpen }),
            React.createElement(DrawerContent, __assign({}, drawerContentProps), props.children),
            collapsible && (React.createElement(DrawerFooter, { anchor: props.anchor || 'left' },
                React.createElement(Button, { "aria-label": "collapsible-button", color: "tertiary", icon: true, onClick: onButtonClick, style: {
                        transform: theme.direction === 'ltr' ? 'rotate(0deg)' : 'rotate(180deg)',
                    } },
                    React.createElement(SvgIcon, { component: drawerOpen ? openedIcon : closedIcon, color: "action" })))))));
};
export default Drawer;
